import React from 'react';

const MessagingConnectionModal = ({
  isOpen,
  onClose,
  messagingConnectionError,
  twilioPhone,
  setTwilioPhone,
  twilioSid,
  setTwilioSid,
  twilioToken,
  setTwilioToken,
  onShortcutsConnect,
  onTwilioConnect
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white mb-1">Connect Your Messages</h3>
          <p className="text-sm text-gray-400">Choose how you want to sync your messages</p>
        </div>
        
        <div className="p-6 space-y-4">
          {messagingConnectionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {messagingConnectionError}
            </div>
          )}
          
          {/* iPhone Shortcuts Option */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-green-500/50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <svg className="w-8 h-8 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold mb-1">iPhone via Shortcuts App</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Sync your iMessage and SMS using Apple Shortcuts automation. Your messages stay on your device.
                </p>
                <div className="space-y-2 text-xs text-gray-500 mb-4">
                  <p>1. Download the Shortcut from our setup page</p>
                  <p>2. Configure it with your dashboard URL</p>
                  <p>3. Messages sync automatically in background</p>
                </div>
                <button
                  onClick={onShortcutsConnect}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="shortcuts-connect-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Connect iPhone
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-700"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-gray-700"></div>
          </div>
          
          {/* Twilio Option */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-red-500/50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#F22F46]/20 rounded-xl">
                <svg className="w-8 h-8 text-[#F22F46]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold mb-1">Twilio SMS</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Connect via Twilio API for SMS messaging. Requires a Twilio account with SMS-enabled number.
                </p>
                <div className="space-y-3 mb-4">
                  <input
                    type="text"
                    value={twilioPhone}
                    onChange={(e) => setTwilioPhone(e.target.value)}
                    placeholder="Phone Number (+1234567890)"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-red-500 focus:outline-none"
                    data-testid="twilio-phone-input"
                  />
                  <input
                    type="text"
                    value={twilioSid}
                    onChange={(e) => setTwilioSid(e.target.value)}
                    placeholder="Account SID"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-red-500 focus:outline-none"
                    data-testid="twilio-sid-input"
                  />
                  <input
                    type="password"
                    value={twilioToken}
                    onChange={(e) => setTwilioToken(e.target.value)}
                    placeholder="Auth Token"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-red-500 focus:outline-none"
                    data-testid="twilio-token-input"
                  />
                </div>
                <button
                  onClick={onTwilioConnect}
                  disabled={!twilioPhone || !twilioSid || !twilioToken}
                  className="w-full bg-[#F22F46] hover:bg-[#d62a3e] disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="twilio-connect-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                  </svg>
                  Connect Twilio
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessagingConnectionModal;
